import {
  AddPlaidAccountInput,
  AddPlaidAccountResultTypenames,
  Mutation,
  PlaidUpdateItemV2Input,
  PlaidUpdateItemV2ResultTypenames,
} from '@varo/graphql-schema-types';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { LinkEventMetadata } from 'react-native-plaid-link-sdk';
import type { PlaidLinkOnEventMetadata } from 'react-plaid-link';
import { AddPlaidAccount } from 'services/graphql/mutations/Plaid/AddPlaidAccount';
import { PlaidOnEvent } from 'services/graphql/mutations/Plaid/PlaidOnEvent';
import { PlaidOnExitEvent } from 'services/graphql/mutations/Plaid/PlaidOnExitEvent';
import { UpdatePlaidAccount } from 'services/graphql/mutations/Plaid/UpdatePlaidAccount';
import {
  PLAID_EXTERNAL_ACCOUNT_LINKED,
  PLAID_INTEGRATION_VERSION_V2,
  PLAID_LINK_ERROR,
  PLAID_LINK_EXITED,
} from 'shared/analytics/events/externalAccount';
import { useAnalytics } from 'shared/hooks/analytics/useAnalytics';
import useCancellableMutation, {
  AbortError,
} from 'shared/hooks/useCancellableMutation';
import { logOnEventAnalytics } from 'shared/hooks/usePlaidV2/utils/onEventAnalytics';
import { useIdempotencyToken } from 'shared/utils/idempotencyTokens/idempotencyTokenUtils';
import { logErrorWithContext } from 'shared/utils/sentry/sentry';
import {
  ErrorSeverity,
  FeatureArea,
  ResponsibleTeam,
} from 'shared/utils/sentry/sentry.constants';
import { CombinedError } from 'urql';

import {
  PlaidCallbackError,
  PlaidExitContext,
  PlaidIntent,
  PlaidIntentTypes,
  PlaidSuccessContext,
} from './types';
import { FetchToken } from './useGetPlaidTokens';

// Telemetry mutations are fire-and-forget, so an in-flight request is aborted
// whenever the caller unmounts or a newer event supersedes it.
const ignoreAbortedTelemetry = (error: unknown) => {
  if (error instanceof AbortError) {
    return;
  }

  logErrorWithContext(error, {
    featureArea: FeatureArea.PLAID,
    responsibleTeam: ResponsibleTeam.FRONTEND_PLATFORM,
    severity: ErrorSeverity.LOW,
  });
};

type UsePlaidV2CallbacksParams = {
  intent: PlaidIntentTypes;
  linkReferenceId?: string;
  refetchTokens: FetchToken;
  sourceName?: string;
};

const usePlaidV2Callbacks = ({
  intent,
  linkReferenceId,
  refetchTokens,
  sourceName,
}: UsePlaidV2CallbacksParams) => {
  const [, addExternalAccount] =
    useCancellableMutation<Mutation>(AddPlaidAccount);
  const [, updateExternalAccount] =
    useCancellableMutation<Mutation>(UpdatePlaidAccount);
  const [, plaidOnEvent] = useCancellableMutation(PlaidOnEvent);
  const [, plaidOnExitEvent] = useCancellableMutation(PlaidOnExitEvent);

  const { logEvent } = useAnalytics();
  const { idempotencyToken } = useIdempotencyToken('0');
  const { t: translatedText } = useTranslation('plaidV2Strings', {
    keyPrefix: 'errors',
  });

  const accountId = 'accountId' in intent ? intent.accountId : undefined;

  const onSuccess = useCallback(
    async ({
      publicToken,
      metadata,
      sdkVersion,
    }: PlaidSuccessContext): Promise<PlaidCallbackError | 'success'> => {
      try {
        const serializedMetadata: string = JSON.stringify({
          linkSessionId: metadata.linkSessionId,
          institutionId: metadata.institutionId,
          institutionName: metadata.institutionName,
          accounts: metadata.accounts,
        });

        if (intent.intent === PlaidIntent.Wait) {
          return 'success';
        }

        if (intent.intent === PlaidIntent.Link) {
          logEvent(PLAID_EXTERNAL_ACCOUNT_LINKED, {
            intent: intent.intent,
            institution_name: metadata.institutionName,
            product: intent.product,
            entry_point: intent.entryPoint,
            source_name: sourceName,
            plaidIntegrationVersion: PLAID_INTEGRATION_VERSION_V2,
          });
          const input: AddPlaidAccountInput = {
            sdkVersion,
            publicToken,
            products: [intent.product],
            metadata: serializedMetadata,
            linkReferenceId,
          };

          const response = await addExternalAccount({
            idempotencyId: idempotencyToken,
            input,
          });

          if (response.error) {
            logEvent(PLAID_LINK_ERROR, {
              intent: intent.intent,
              error_type: 'system',
              error_step: 'mutation_add',
              error_code: response.error.name,
              error_message: response.error.message,
              source_name: sourceName,
              plaidIntegrationVersion: PLAID_INTEGRATION_VERSION_V2,
            });
            return {
              name: response.error.name ?? 'AddPlaidAccountError',
              message:
                response.error.message ?? translatedText('addAccountFailed'),
            };
          }

          const typename = response.data?.addPlaidAccount?.__typename;

          if (
            typename !== AddPlaidAccountResultTypenames.AddPlaidAccountSuccess
          ) {
            logEvent(PLAID_LINK_ERROR, {
              intent: intent.intent,
              error_type: 'validation',
              error_step: 'mutation_add',
              error_code: typename,
              error_message: response.data?.addPlaidAccount?.message,
              source_name: sourceName,
              plaidIntegrationVersion: PLAID_INTEGRATION_VERSION_V2,
            });
            return {
              name:
                response.data?.addPlaidAccount?.title ?? 'AddPlaidAccountError',
              message:
                response.data?.addPlaidAccount?.message ??
                translatedText('addAccountUnavailable'),
            };
          }

          return 'success';
        }

        if (!accountId) {
          return {
            name: 'MissingAccountIdError',
            message: translatedText('missingAccountId'),
          };
        }

        const input: PlaidUpdateItemV2Input = {
          accountId,
          institutionId: metadata.institutionId ?? '',
          metadata: serializedMetadata,
          linkReferenceId,
        };

        const response = await updateExternalAccount({ input });

        if (response.error) {
          logEvent(PLAID_LINK_ERROR, {
            intent: intent.intent,
            error_type: 'system',
            error_step: 'mutation_update',
            error_code: response.error.name,
            error_message: response.error.message,
            source_name: sourceName,
            plaidIntegrationVersion: PLAID_INTEGRATION_VERSION_V2,
          });
          return {
            name: response.error.name ?? 'UpdatePlaidAccountError',
            message:
              response.error.message ?? translatedText('updateAccountFailed'),
          };
        }

        const typename = response.data?.plaidUpdateItemV2?.__typename;

        if (
          typename !== PlaidUpdateItemV2ResultTypenames.PlaidUpdateItemV2Success
        ) {
          logEvent(PLAID_LINK_ERROR, {
            intent: intent.intent,
            error_type: 'validation',
            error_step: 'mutation_update',
            error_code: typename,
            error_message: response.data?.plaidUpdateItemV2?.message,
            source_name: sourceName,
            plaidIntegrationVersion: PLAID_INTEGRATION_VERSION_V2,
          });
          return {
            name: 'PlaidUpdateItemV2Error',
            message:
              response.data?.plaidUpdateItemV2?.message ??
              translatedText('updateAccountUnavailable'),
          };
        }

        return 'success';
      } catch (error) {
        logEvent(PLAID_LINK_ERROR, {
          intent: intent.intent,
          error_type: error instanceof AbortError ? 'timeout' : 'system',
          error_step:
            intent.intent === PlaidIntent.Link
              ? 'mutation_add'
              : 'mutation_update',
          error_code: error instanceof Error ? error.name : 'UnknownError',
          error_message: error instanceof Error ? error.message : undefined,
          source_name: sourceName,
          plaidIntegrationVersion: PLAID_INTEGRATION_VERSION_V2,
        });
        if (error instanceof AbortError) {
          return {
            name: 'AbortError',
            message: translatedText('operationCancelled'),
          };
        }
        return {
          name:
            error instanceof Error || error instanceof CombinedError
              ? error.name
              : 'UnknownError',
          message:
            error instanceof Error || error instanceof CombinedError
              ? error.message
              : translatedText('unexpectedError'),
        };
      }
    },
    [
      accountId,
      addExternalAccount,
      idempotencyToken,
      intent,
      linkReferenceId,
      logEvent,
      sourceName,
      translatedText,
      updateExternalAccount,
    ],
  );

  const onExit = useCallback(
    (context: PlaidExitContext): undefined | PlaidCallbackError => {
      logEvent(PLAID_LINK_EXITED, {
        intent: intent.intent,
        exit_status: context.errorCode
          ? 'error'
          : (context.metadata?.exitStatus ?? 'cancelled'),
        link_session_id: context.metadata?.linkSessionId,
        error_code: context.errorCode,
        error_message: context.errorMessage,
        institution_name: context.metadata?.institutionName,
        source_name: sourceName,
        plaidIntegrationVersion: PLAID_INTEGRATION_VERSION_V2,
      });

      const input = {
        accountId,
        error: JSON.stringify({
          errorCode: context.errorCode ?? null,
          errorMessage: context.errorMessage,
        }),
        metadata: JSON.stringify(context.metadata),
      };

      void plaidOnExitEvent({ input }).catch(ignoreAbortedTelemetry);

      if (context.errorCode) {
        logEvent(PLAID_LINK_ERROR, {
          step_name: context.errorCode?.toLocaleLowerCase() ?? '',
          session_id: context.metadata?.linkSessionId ?? '',
          id: context.metadata?.institutionId ?? '',
          institution_name: context.metadata?.institutionName ?? '',
          exit_status: context.metadata?.exitStatus ?? '',
          error_message: context.errorMessage ?? '',
          plaidIntegrationVersion: PLAID_INTEGRATION_VERSION_V2,
        });

        if (context.errorCode === 'INVALID_LINK_TOKEN') {
          refetchTokens();
        }

        return {
          name: context.errorCode ?? 'Plaid Exit Error',
          message: context.errorMessage ?? translatedText('unexpectedError'),
        };
      }
    },
    [
      accountId,
      intent,
      logEvent,
      plaidOnExitEvent,
      refetchTokens,
      sourceName,
      translatedText,
    ],
  );

  const onEvent = useCallback(
    (
      eventName: string,
      metadata: LinkEventMetadata | PlaidLinkOnEventMetadata,
    ) => {
      const input = {
        accountId,
        eventName,
        metadata: JSON.stringify(metadata),
      };
      void plaidOnEvent({ input }).catch(ignoreAbortedTelemetry);

      logOnEventAnalytics({
        logEvent,
        eventName,
        metadata,
        intent,
        sourceName: sourceName ?? undefined,
        plaidIntegrationVersion: PLAID_INTEGRATION_VERSION_V2,
      });
    },
    [accountId, intent, logEvent, plaidOnEvent, sourceName],
  );

  return {
    onSuccess,
    onExit,
    onEvent,
  };
};

export default usePlaidV2Callbacks;
